import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId, useState } from "react";
import { notifyError } from "../../utils/notifications";
import { LeafletHeatmap } from "./LeafletHeatmap";
import { VesselsModal } from "./VesselsModal";
import { WorldMapLegend } from "./WorldMapLegend";
import { handleViewportChange, mapLocationsToTuples, } from "./heatmapHelper";
const MAP_OPTIONS = {
    minZoom: 2,
    maxZoom: 11,
    initialZoomLevel: 2,
    attributionControl: true,
    dragging: true,
    doubleClickZoom: true,
    scrollWheelZoom: true,
    zoomControl: true,
    markerMinZoomLevel: -10,
    zoneMinZoomLevel: 6,
    showLocations: true,
    showMarkersOnlyOnHover: true,
    locationLinkTo: "RegionalStats",
    initialCenter: [25, 0],
    maxBounds: [
        [-90, -180],
        [90, 180],
    ],
};
export const LevelsWorldMap = ({ options }) => {
    const [ncVesselsList, setNcVesselsList] = useState([]);
    const [levelData, setLevelData] = useState({
        Level1: [],
        Level2: [],
        Level3: [],
        Level4: [],
    });
    const [locationData, setLocationData] = useState([]);
    const [levelButtons, setLevelButtons] = useState({
        l1: true,
        l2: false,
        l3: false,
        l4: false,
    });
    const mapId = useId().replace(/:/g, "");
    const [viewport, setViewport] = useState({ zoomLevel: MAP_OPTIONS.initialZoomLevel });
    const selectedLevels = [
        levelButtons.l1 ? 1 : null,
        levelButtons.l2 ? 2 : null,
        levelButtons.l3 ? 3 : null,
        levelButtons.l4 ? 4 : null,
    ].filter((level) => level !== null);
    const ncPoints = [
        ...(levelButtons.l1 ? levelData.Level1 : []),
        ...(levelButtons.l2 ? levelData.Level2 : []),
        ...(levelButtons.l3 ? levelData.Level3 : []),
        ...(levelButtons.l4 ? levelData.Level4 : []),
    ];
    const reloadLocations = async (newOptions, levels) => {
        if (!newOptions?.isReady) {
            return;
        }
        const params = new URLSearchParams({
            fromMonth: newOptions.startDate.substring(0, 7),
            toMonth: newOptions.endDate.substring(0, 7),
            fleets: newOptions.fleet.map((fleet) => fleet.ID).join(","),
            levels: levels.join(","),
        });
        try {
            const response = await fetch(`/api/levelsbenchmark/portperformances?${params}`);
            if (!response.ok) {
                throw new Error(response.statusText);
            }
            const locations = await response.json();
            setLocationData(mapLocationsToTuples(locations));
        }
        catch {
            notifyError("An error has occurred when loading stats. Please try again.");
        }
    };
    const reloadNcVessels = async (newOptions, levels, currentViewport) => {
        if (!newOptions?.isReady || !currentViewport.northWest || !currentViewport.southEast) {
            return;
        }
        setNcVesselsList([]);
        const params = new URLSearchParams({
            fromMonth: newOptions.startDate.substring(0, 7),
            toMonth: newOptions.endDate.substring(0, 7),
            fleets: newOptions.fleet.map((fleet) => fleet.ID).join(","),
            minLatitude: String(currentViewport.southEast.lat),
            maxLatitude: String(currentViewport.northWest.lat),
            minLongitude: String(currentViewport.northWest.lng),
            maxLongitude: String(currentViewport.southEast.lng),
            levels: levels.join(","),
        });
        try {
            const response = await fetch(`/api/levelsbenchmark/ncvessels?${params}`);
            if (!response.ok) {
                throw new Error(response.statusText);
            }
            setNcVesselsList(await response.json());
        }
        catch {
            setNcVesselsList([]);
            notifyError("An error has occurred when loading NC vessel list. Please try again.");
        }
    };
    useEffect(() => {
        if (!options.isReady)
            return;
        const params = new URLSearchParams({
            fromMonth: options.startDate.substring(0, 7),
            toMonth: options.endDate.substring(0, 7),
            fleets: options.fleet.map((fleet) => fleet.ID).join(","),
        });
        fetch(`/api/levelsbenchmark/heatmap?${params}`)
            .then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error(response.statusText);
        })
            .then((stats) => {
            setLevelData(stats);
        })
            .catch(() => {
            notifyError("An error has occurred when loading stats. Please try again.");
        });
    }, [options, options.isReady, options.startDate, options.endDate, options.fleet.map((fleet) => fleet.ID).join(",")]);
    useEffect(() => {
        void reloadLocations(options, selectedLevels);
    }, [
        options,
        options.isReady,
        options.startDate,
        options.endDate,
        options.fleet.map((fleet) => fleet.ID).join(","),
        selectedLevels.join(","),
    ]);
    useEffect(() => {
        void reloadNcVessels(options, selectedLevels, viewport);
    }, [
        options,
        options.isReady,
        options.startDate,
        options.endDate,
        options.fleet.map((fleet) => fleet.ID).join(","),
        selectedLevels.join(","),
        viewport.northWest?.lat,
        viewport.northWest?.lng,
        viewport.southEast?.lat,
        viewport.southEast?.lng,
        viewport.zoomLevel,
    ]);
    const toggleLevel = (levelKey) => {
        setLevelButtons((currentState) => ({
            ...currentState,
            [levelKey]: !currentState[levelKey],
        }));
    };
    return (_jsxs("div", { className: "position-relative card shadow", children: [_jsx(LeafletHeatmap, { id: mapId, options: MAP_OPTIONS, data: ncPoints, locations: locationData, params: options, onViewportChange: (nextViewport) => handleViewportChange(nextViewport, setViewport), className: "w-100 mb-1 card-body", style: { height: "530px" } }), _jsx(LevelSelector, { levelButtons: levelButtons, onToggleL1: () => toggleLevel("l1"), onToggleL2: () => toggleLevel("l2"), onToggleL3: () => toggleLevel("l3"), onToggleL4: () => toggleLevel("l4") }), _jsx(WorldMapLegend, { userMode: true }), _jsx(VesselsModal, { ncVesselsList: ncVesselsList })] }));
};
const LevelSelector = ({ levelButtons, onToggleL1, onToggleL2, onToggleL3, onToggleL4, }) => {
    return (_jsx("div", { style: { top: 0, zIndex: 500 }, className: "position-absolute m-3", children: _jsxs("div", { className: "btn-group mb-3", role: "group", "aria-label": "NC level selector", children: [_jsx(LevelToggleButton, { label: "L1", isActive: levelButtons.l1, onClick: onToggleL1 }), _jsx(LevelToggleButton, { label: "L2", isActive: levelButtons.l2, onClick: onToggleL2 }), _jsx(LevelToggleButton, { label: "L3", isActive: levelButtons.l3, onClick: onToggleL3 }), _jsx(LevelToggleButton, { label: "L4", isActive: levelButtons.l4, onClick: onToggleL4 })] }) }));
};
const LevelToggleButton = ({ label, isActive, onClick }) => {
    return (_jsx("button", { type: "button", className: `btn ${isActive ? "btn-primary" : "bg-white btn-outline-primary"}`, onClick: onClick, children: label }));
};
